import { Button, Checkbox, Group, Modal, Stack, Text, ScrollArea, Alert, Table } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import { IconUser } from '@tabler/icons-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AiInfoItemWithMembers } from '@mygpt/app/collaborate/services';
import { batchTransferRobots } from 'packages/app/apis/services/robots';
import LocalMemberSelector from '@/components/Access/LocalMemberSelector';
import { Datasets } from '@/components/Access/TransferModal/Datasets';
import { useAdminStore } from '@/store/admin';

interface TransferModalProps {
  opened: boolean;
  onClose: () => void;
  selectedItems: AiInfoItemWithMembers[];
  onRefresh: () => void;
}

const TransferModal = ({ opened, onClose, selectedItems, onRefresh }: TransferModalProps) => {
  const { t } = useTranslation();
  const [targetUser, setTargetUser] = useState<any>(null);
  const [clearCollaborators, setClearCollaborators] = useState(true);
  const [forceTransferDatasets, setForceTransferDatasets] = useState(true);
  const [loading, setLoading] = useState(false);
  const [userSelectorOpened, setUserSelectorOpened] = useState(false);
  const { fetchAllAdminData } = useAdminStore();

  const handleUserSelect = (users: any) => {
    if (users.length > 0) {
      setTargetUser(users[0]);
      setUserSelectorOpened(false);
    }
  };

  const handleTransfer = async () => {
    if (!targetUser) {
      showNotification({
        message: t('access.transfer.validate.user_required'),
        color: 'red',
      });
      return;
    }

    setLoading(true);
    try {
      const botIds = selectedItems.map((item) => item.ai.id);

      await batchTransferRobots({
        bot_ids: botIds,
        user_id: targetUser.uuid.includes('|') ? targetUser.uuid : `gptbase|${targetUser.uuid}`, // 组织权限这边创建的用户，都没有返回 xxx| 前面这一段，线上cloud版本的 user_id 字段是有这个的。因此前端这里做下判断
        clear_collaborators: clearCollaborators,
        force_transfer_dataset: forceTransferDatasets,
      });

      showNotification({
        message: t('access.transfer.success'),
        color: 'green',
      });

      handleClose();
      onRefresh();
    } catch (error: any) {
      console.error(error?.message);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setTargetUser(null);
    setClearCollaborators(true);
    setLoading(false);
    onClose();
  };

  const openUserSelector = () => {
    // 每次打开弹窗，都重新拉取最新的管理员数据
    // 因为当前组织权限用的数据和选择框的数据不在一起，两边不同步。
    // 后续应该使用统一数据。
    // 先简单重新请求数据来获取可能在组织权限那边删除后的最新数据。
    fetchAllAdminData(true);
    setUserSelectorOpened(true);
  };

  return (
    <>
      <Modal
        opened={opened}
        onClose={handleClose}
        title={t('access.transfer.title')}
        centered
        size="xl"
        withCloseButton
      >
        <Stack gap="md">
          <Group>
            <Text>{t('access.transfer.target_user')}:</Text>
            {targetUser ? (
              <Group>
                <Text>{targetUser.email}</Text>
                <Button variant="light" onClick={() => setTargetUser(null)}>
                  {t('access.transfer.select_user_button')}
                </Button>
              </Group>
            ) : (
              <Button variant="light" size="xs" onClick={openUserSelector} leftSection={<IconUser size={16} />}>
                {t('access.transfer.select_user_button')}
              </Button>
            )}
          </Group>

          <Text>{t('access.transfer.selected_robots', { count: selectedItems.length })}:</Text>
          {/* Chatbot 信息卡片 */}
          <ScrollArea.Autosize
            mah={400}
            type="auto"
            styles={{
              viewport: {
                background: '#F9FAFB',
              },
            }}
          >
            <Table layout="fixed">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th w={200}>{t('access.transfer.robot_name')}</Table.Th>
                  <Table.Th>{t('access.transfer.datasets')}</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {selectedItems.map((item) => (
                  <Table.Tr key={item.ai.id}>
                    <Table.Td>
                      <Text>{item.ai.name}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Datasets id={item.ai.id} />
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </ScrollArea.Autosize>

          <Checkbox
            label={t('access.transfer.clear_collaborators')}
            description={t('access.transfer.clear_collaborators_desc')}
            checked={clearCollaborators}
            onChange={(e) => setClearCollaborators(e.currentTarget.checked)}
            color="violet"
          />

          <Checkbox
            label={t('access.transfer.force_transfer_datasets')}
            checked={forceTransferDatasets}
            onChange={(e) => setForceTransferDatasets(e.currentTarget.checked)}
            color="violet"
          />

          {/* 警告提示 */}
          <Group gap={8}>
            <Alert variant="light" color="yellow" title="" className="w-full">
              {t('access.transfer.warning')}
            </Alert>
          </Group>

          {/* 确认按钮 */}
          <Group justify="flex-end" mt={8}>
            <Button variant="outline" onClick={handleClose}>
              {t('access.transfer.cancel')}
            </Button>
            <Button onClick={handleTransfer} loading={loading} disabled={!targetUser}>
              {t('access.transfer.button')}
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* 用户选择器 */}
      {userSelectorOpened && (
        <LocalMemberSelector
          key="transfer-user-selector"
          open={userSelectorOpened}
          onClose={() => setUserSelectorOpened(false)}
          onConfirm={handleUserSelect}
          maxSelect={1}
        />
      )}
    </>
  );
};

export default TransferModal;
